import { useEffect, useRef, useState } from 'react'

// Sticky in-page section nav. `sections` = [{ label, id }] — each id must exist
// on a wrapper element (with className "anchor-target" for scroll offset).
export default function TrekSectionNav({ sections }) {
  const [active, setActive] = useState(sections[0]?.id)
  const tabRefs = useRef({})
  const rowRef = useRef(null)

  // Scroll-spy: a section becomes active when its top passes just under the
  // sticky navbar + tab bar (~120px), long before it leaves the viewport.
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActive(entry.target.id)
        })
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 }
    )
    sections.forEach(s => {
      const el = document.getElementById(s.id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [sections])

  // Keep the active tab visible inside the horizontally scrollable row.
  // Adjust the row's own scrollLeft rather than calling scrollIntoView, which
  // would walk up to the document and scroll the page out from under the reader.
  useEffect(() => {
    const el = tabRefs.current[active]
    const row = rowRef.current
    if (!el || !row) return
    if (row.scrollWidth <= row.clientWidth) return   // nothing to scroll

    const left = el.offsetLeft
    const right = left + el.offsetWidth
    if (left < row.scrollLeft) {
      row.scrollLeft = left
    } else if (right > row.scrollLeft + row.clientWidth) {
      row.scrollLeft = right - row.clientWidth
    }
  }, [active])

  const go = id => {
    setActive(id)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <nav aria-label="Page sections" style={{
      position: 'sticky', top: '62px', zIndex: 90,
      background: 'var(--white)', borderBottom: '1px solid var(--stone-mid)',
      padding: '0 5%',
    }}>
      <div className="scroll-row" ref={rowRef} style={{ gap: '4px', paddingBottom: 0 }}>
        {sections.map(s => {
          const isActive = active === s.id
          return (
            <button
              key={s.id}
              ref={el => { tabRefs.current[s.id] = el }}
              onClick={() => go(s.id)}
              className="scroll-row-item"
              aria-current={isActive ? 'true' : undefined}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '14px 14px 12px',
                fontFamily: 'var(--sans)', fontSize: '13px',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--green)' : 'var(--ink-mid)',
                borderBottom: isActive ? '2px solid var(--green)' : '2px solid transparent',
                transition: 'var(--transition)',
                whiteSpace: 'nowrap',
              }}
            >
              {s.label}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
